import { create } from "zustand";

/**
 * Store for managing drag state across the 3D viewer.
 * Tracks which part is being dragged and provides centralized state management.
 */
interface DragState {
  /** The ID of the part currently being dragged, or null if no drag is active */
  draggingPartId: string | null;
  /**
   * Whether a drag operation is currently active (used to suppress click
   * events). Deliberately stays true for 100 ms after the pointer comes up, so
   * the click that ends a drag is swallowed — see {@link DragState.endDrag}.
   * Consumers that want a gesture to *settle* before they go back to full
   * quality (shadow refresh, adaptive resolution, the floor re-seat) key off
   * this one.
   */
  isDragActive: boolean;
  /**
   * Whether the pointer is down on a part right now. Set and cleared with the
   * gesture itself, without the 100 ms tail.
   *
   * Anything that *stops accepting input* while a drag runs has to key off
   * this rather than {@link DragState.isDragActive}: holding it over the tail
   * swallows a press that starts the next drag, and a gesture that never
   * delivers its `pointerup` would hold it forever.
   */
  isGestureInFlight: boolean;

  /** Start dragging a specific part */
  startDrag: (partId: string) => void;
  /** End the current drag operation */
  endDrag: () => void;
  /**
   * Drop the session without the click-suppression tail, for a gesture that
   * can no longer deliver its `pointerup` — the window was switched while the
   * button was down. Clears both flags at once: there is no click left to
   * swallow, and leaving them set keeps the scene on its interacting shadow
   * and floor for the rest of the session.
   */
  cancelDrag: () => void;
}

/**
 * The pending {@link DragState.endDrag} reset, so a gesture that starts inside
 * the tail of the previous one can cancel it. Without that, ending drag A and
 * starting drag B 50 ms later lets A's timer clear `isDragActive` in the middle
 * of B, and the shadow, floor and post-processing drop back to settled quality
 * while the pointer is still down.
 */
let pendingReset: ReturnType<typeof setTimeout> | null = null;

function clearPendingReset(): void {
  if (pendingReset !== null) {
    clearTimeout(pendingReset);
    pendingReset = null;
  }
}

export const useDragStore = create<DragState>((set) => ({
  draggingPartId: null,
  isDragActive: false,
  isGestureInFlight: false,

  startDrag: (partId: string) => {
    clearPendingReset();
    set({
      draggingPartId: partId,
      isDragActive: true,
      isGestureInFlight: true,
    });
  },

  endDrag: () => {
    // The gesture itself is over now; only the click suppression waits.
    clearPendingReset();
    set({ isGestureInFlight: false });
    // Small delay before clearing drag state to allow click suppression
    pendingReset = setTimeout(() => {
      pendingReset = null;
      set({ draggingPartId: null, isDragActive: false });
    }, 100);
  },

  cancelDrag: () => {
    clearPendingReset();
    set({
      draggingPartId: null,
      isDragActive: false,
      isGestureInFlight: false,
    });
  },
}));
